import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';

const PLAQUE_OPTIONS = [
  'None',
  'Mild',
  'Moderate',
  'Severe',
  'Occluded'
];

const STENOSIS_OPTIONS = [
  'None',
  'Less than 50%',
  '50-69%',
  '>70% but less than near occlusion',
  'Near occlusion',
  'Total occlusion'
];

const VERTEBRAL_OPTIONS = [
  'Normal antegrade flow',
  'Retrograde flow',
  'Biphasic (Rabbit Ears)',
  'Not Detectable'
];

// Function to calculate Doppler stenosis based on measurements
function calculateDopplerStenosis(psv, edv, icaCcaRatio, stenosis2D) {
  const psvNum = parseFloat(psv) || 0;
  const icaRatio = parseFloat(icaCcaRatio) || 0;
  
  // Check for Near Occlusion or Total Occlusion first
  if (stenosis2D === 'Near occlusion') {
    return 'Near occlusion';
  }
  if (stenosis2D === 'Total occlusion') {
    return 'Total occlusion';
  }
  
  // >70% stenosis: PSV > 230, 2D stenosis at least "50-69%", ICA:CCA > 4.0
  if (psvNum > 230 && icaRatio > 4.0 && 
      (stenosis2D === '50-69%' || stenosis2D === '>70% but less than near occlusion')) {
    return '>70% but less than near occlusion';
  }
  
  // 50-69% stenosis: 
  // (PSV 180-230, 2D "50-69%", ICA:CCA 2.0-4.0) OR (PSV 125-180 and ICA:CCA >= 2.0)
  if ((psvNum >= 180 && psvNum <= 230 && stenosis2D === '50-69%' && icaRatio >= 2.0 && icaRatio <= 4.0) ||
      (psvNum >= 125 && psvNum <= 180 && icaRatio >= 2.0)) {
    return '50-69%';
  }
  
  // Less than 50%: PSV < 180, ICA:CCA < 2.0, 2D "Less than 50%"
  if (psvNum < 180 && icaRatio < 2.0 && stenosis2D === 'Less than 50%') {
    return 'Less than 50%';
  }
  
  // Normal: PSV < 180, ICA:CCA < 2.0, 2D "None"
  if (psvNum < 180 && icaRatio < 2.0 && stenosis2D === 'None') {
    return 'None';
  }
  
  return 'Indeterminate';
}

// Initial form data for Carotid form
const INITIAL_CAROTID_DATA = {
  examDate: new Date().toISOString().split('T')[0],
  firstName: '',
  lastName: '',
  mrn: '',
  technologist: '',
  reason: '',
  callReport: false,
  phoneNumber: '',
  history: '',
  comments: '',
  rightCarotid: {
    plaque: '',
    psv: '',
    edv: '',
    icaCcaRatio: '',
    stenosis2D: '',
    stenosisDoppler: '',
    endarterectomy: false,
    other: ''
  },
  leftCarotid: {
    plaque: '',
    psv: '',
    edv: '',
    icaCcaRatio: '',
    stenosis2D: '',
    stenosisDoppler: '',
    endarterectomy: false,
    other: ''
  },
  vertebralArteries: {
    right: 'Normal antegrade flow',
    left: 'Normal antegrade flow'
  }
};

function CarotidForm({ token, onBack, facilityInfo }) {
  // Tab state: 0=Right Carotid, 1=Left Carotid, 2=Vertebral Arteries
  const [activeTab, setActiveTab] = useState(0);
  // Fetch dynamic technologists list
    const { technologists, defaultTech } = useTechnologists();

  // Use the custom hook
  const {
    formData,
    updateField,
    updateNestedField,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    resetForm,
    isLoading,
    error,
    success,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(
    {
      ...INITIAL_CAROTID_DATA,
      technologist: defaultTech
    },
    'Carotid',
    {
      token, facilityInfo
    }
  );

  // Update technologist field when defaultTech loads
    useEffect(() => {
      if (defaultTech && !formData.technologist) {
        updateField('technologist', defaultTech);
      }
    }, [defaultTech, formData.technologist, updateField]);
  

  // Calculate Doppler stenosis on form submission
  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Calculate Doppler stenosis values and update formData synchronously
    const rightStenosisDoppler = calculateDopplerStenosis(
      formData.rightCarotid.psv,
      formData.rightCarotid.edv,
      formData.rightCarotid.icaCcaRatio,
      formData.rightCarotid.stenosis2D
    );
    
    const leftStenosisDoppler = calculateDopplerStenosis(
      formData.leftCarotid.psv,
      formData.leftCarotid.edv,
      formData.leftCarotid.icaCcaRatio,
      formData.leftCarotid.stenosis2D
    );
    
    // Update the values in formData
    updateNestedField('rightCarotid', 'stenosisDoppler', rightStenosisDoppler);
    updateNestedField('leftCarotid', 'stenosisDoppler', leftStenosisDoppler);
    
    // Small delay to ensure state updates are processed
    setTimeout(() => {
      hookHandleSubmit(e);
    }, 100);
  }, [formData, updateNestedField, hookHandleSubmit]);

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={handleFormSubmit}>
        {/* Status Messages */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {/* Draft restore banner */}
        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        {/* History */}
        <div className="form-section">
          <h3>History</h3>
          <div className="form-group">
            <label>Clinical History:</label>
            <input
              type="text"
              value={formData.history}
              onChange={(e) => updateField('history', e.target.value)}
              placeholder="Enter clinical history"
            />
          </div>
        </div>

        {/* TAB NAVIGATION */}
        <div className="form-section">
          <div className="worksheet-tabs">
            <button
              type="button"
              onClick={() => setActiveTab(0)}
              className={`worksheet-tab-btn${activeTab === 0 ? ' active' : ''}`}
            >
              Right Carotid
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(1)}
              className={`worksheet-tab-btn${activeTab === 1 ? ' active' : ''}`}
            >
              Left Carotid
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(2)}
              className={`worksheet-tab-btn${activeTab === 2 ? ' active' : ''}`}
            >
              Vertebral Arteries
            </button>
          </div>

          {/* Tab Content */}
          <div className="worksheet-tab-content" style={{ minHeight: '400px' }}>
            {/* Right Carotid Tab */}
            {activeTab === 0 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Right Carotid</h3>
          
          <div className="form-group">
            <label>Plaque:</label>
            <select
              value={formData.rightCarotid.plaque}
              onChange={(e) => updateNestedField('rightCarotid.plaque', e.target.value)}
            >
              <option value="">Select plaque status</option>
              {PLAQUE_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>ICA PSV (Peak Systolic Velocity) cm/sec:</label>
            <input
              type="number"
              step="0.1"
              value={formData.rightCarotid.psv}
              onChange={(e) => updateNestedField('rightCarotid.psv', e.target.value)}
              placeholder="Peak systolic velocity"
              title="Peak systolic velocity should be obtained at level of ICA stenosis"
            />
          </div>

          <div className="form-group">
            <label>ICA EDV (End Diastolic Velocity) cm/sec:</label>
            <input
              type="number"
              step="0.1"
              value={formData.rightCarotid.edv}
              onChange={(e) => updateNestedField('rightCarotid.edv', e.target.value)}
              placeholder="End diastolic velocity"
              title="End diastolic velocity should be obtained from same measured waveform used for the PSV measurement"
            />
          </div>

          <div className="form-group">
            <label>ICA:CCA Ratio:</label>
            <input
              type="number"
              step="0.01"
              value={formData.rightCarotid.icaCcaRatio}
              onChange={(e) => updateNestedField('rightCarotid.icaCcaRatio', e.target.value)}
              placeholder="ICA:CCA ratio"
              title="ICA:CCA ratio should be obtained from PSV and EDV measurements above"
            />
          </div>

          <div className="form-group">
            <label>2D Stenosis:</label>
            <select
              value={formData.rightCarotid.stenosis2D}
              onChange={(e) => updateNestedField('rightCarotid.stenosis2D', e.target.value)}
              title="Should be obtained from 2 dimensional image obtained at level of greatest luminal stenosis"
            >
              <option value="">Select stenosis level</option>
              {STENOSIS_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Doppler Stenosis (Calculated):</label>
            <div style={{ 
              padding: '8px 12px', 
              backgroundColor: 'var(--color-surface)', 
              border: '1px solid var(--color-border)', 
              borderRadius: '4px',
              minHeight: '38px',
              display: 'flex',
              alignItems: 'center',
              fontWeight: 'bold'
            }}>
              {calculateDopplerStenosis(
                formData.rightCarotid.psv,
                formData.rightCarotid.edv,
                formData.rightCarotid.icaCcaRatio,
                formData.rightCarotid.stenosis2D
              )}
            </div>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.rightCarotid.endarterectomy}
                onChange={(e) => updateNestedField('rightCarotid.endarterectomy', e.target.checked)}
              />
              {' '}Endarterectomy
            </label>
          </div>

          <div className="form-group">
            <label>Other:</label>
            <input
              type="text"
              value={formData.rightCarotid.other}
              onChange={(e) => updateNestedField('rightCarotid.other', e.target.value)}
              placeholder="Additional findings"
            />
          </div>
              </div>
            )}

            {/* Left Carotid Tab */}
            {activeTab === 1 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Left Carotid</h3>
          
          <div className="form-group">
            <label>Plaque:</label>
            <select
              value={formData.leftCarotid.plaque}
              onChange={(e) => updateNestedField('leftCarotid.plaque', e.target.value)}
            >
              <option value="">Select plaque status</option>
              {PLAQUE_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>ICA PSV (Peak Systolic Velocity) cm/sec:</label>
            <input
              type="number"
              step="0.1"
              value={formData.leftCarotid.psv}
              onChange={(e) => updateNestedField('leftCarotid.psv', e.target.value)}
              placeholder="Peak systolic velocity"
              title="Peak systolic velocity should be obtained at level of ICA stenosis"
            />
          </div>

          <div className="form-group">
            <label>ICA EDV (End Diastolic Velocity) cm/sec:</label>
            <input
              type="number"
              step="0.1"
              value={formData.leftCarotid.edv}
              onChange={(e) => updateNestedField('leftCarotid.edv', e.target.value)}
              placeholder="End diastolic velocity"
              title="End diastolic velocity should be obtained from same measured waveform used for the PSV measurement"
            />
          </div>

          <div className="form-group">
            <label>ICA:CCA Ratio:</label>
            <input
              type="number"
              step="0.01"
              value={formData.leftCarotid.icaCcaRatio}
              onChange={(e) => updateNestedField('leftCarotid.icaCcaRatio', e.target.value)}
              placeholder="ICA:CCA ratio"
              title="ICA:CCA ratio should be obtained from PSV and EDV measurements above"
            />
          </div>

          <div className="form-group">
            <label>2D Stenosis:</label>
            <select
              value={formData.leftCarotid.stenosis2D}
              onChange={(e) => updateNestedField('leftCarotid.stenosis2D', e.target.value)}
              title="Should be obtained from 2 dimensional image obtained at level of greatest luminal stenosis"
            >
              <option value="">Select stenosis level</option>
              {STENOSIS_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Doppler Stenosis (Calculated):</label>
            <div style={{ 
              padding: '8px 12px', 
              backgroundColor: 'var(--color-surface)', 
              border: '1px solid var(--color-border)', 
              borderRadius: '4px',
              minHeight: '38px',
              display: 'flex',
              alignItems: 'center',
              fontWeight: 'bold'
            }}>
              {calculateDopplerStenosis(
                formData.leftCarotid.psv,
                formData.leftCarotid.edv,
                formData.leftCarotid.icaCcaRatio,
                formData.leftCarotid.stenosis2D
              )}
            </div>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.leftCarotid.endarterectomy}
                onChange={(e) => updateNestedField('leftCarotid.endarterectomy', e.target.checked)}
              />
              {' '}Endarterectomy
            </label>
          </div>

          <div className="form-group">
            <label>Other:</label>
            <input
              type="text"
              value={formData.leftCarotid.other}
              onChange={(e) => updateNestedField('leftCarotid.other', e.target.value)}
              placeholder="Additional findings"
            />
          </div>
              </div>
            )}

            {/* Vertebral Arteries Tab */}
            {activeTab === 2 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Vertebral Arteries</h3>
          
          <div className="form-group">
            <label>Right Vertebral Artery:</label>
            <select
              value={formData.vertebralArteries.right}
              onChange={(e) => updateNestedField('vertebralArteries.right', e.target.value)}
            >
              <option value="">Select flow status</option>
              {VERTEBRAL_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Left Vertebral Artery:</label>
            <select
              value={formData.vertebralArteries.left}
              onChange={(e) => updateNestedField('vertebralArteries.left', e.target.value)}
            >
              <option value="">Select flow status</option>
              {VERTEBRAL_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
              </div>
            )}
          </div>
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        {/* Submit */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={isLoading}
            className="preview-btn"
          >
            Preview Report
          </button>
          <button type="submit" disabled={isLoading} className="submit-btn">
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button type="button" onClick={resetForm} className="clear-btn">
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default CarotidForm;
